import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { ADMIN_APPS_KEY } from "./useTopShopAdmin";

export function invalidateTopShopAdmin(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ADMIN_APPS_KEY });
  qc.invalidateQueries({ queryKey: ["admin", "top-shop", "challenge-matrix"] });
  qc.invalidateQueries({ queryKey: ["admin", "top-shop-applicant-ids"] });
}

export function MoveCohortButton({ programId, acceptedCount, alreadyDoneCount }: {
  programId: string; acceptedCount: number; alreadyDoneCount: number;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    const { data, error } = await supabase.rpc("admin_move_cohort_to_community", { p_program_id: programId });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success(`Moved ${data ?? 0} people to the EAB Connect community`);
    setOpen(false);
    invalidateTopShopAdmin(qc);
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Users className="h-4 w-4 mr-2" />Move cohort to community
      </Button>
      <AlertDialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move cohort to community?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>{acceptedCount} accepted {acceptedCount === 1 ? "person" : "people"} will be marked Completed.</p>
                <p>{acceptedCount + alreadyDoneCount} completed or withdrawn {acceptedCount + alreadyDoneCount === 1 ? "person" : "people"} will be moved from the cohort to regular EAB Connect membership.</p>
                <p>Their courses, progress, certificates and badges are kept, and their courses become self-paced. Completed shops stay on the leaderboard.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button onClick={run} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Move cohort
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
